import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, Input, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { TranslateModule } from '@ngx-translate/core';
import { CheckboxModule } from 'primeng/checkbox';
import { TableModule } from 'primeng/table';
import { SkeletonModule } from 'primeng/skeleton';
import { subDays } from 'date-fns';
import { Paginator, PaginatorModule } from 'primeng/paginator';
import { ButtonModule } from 'primeng/button';
import { debounceTime, distinctUntilChanged, Subscription, tap } from 'rxjs';

import { DatePickerComponent } from '@src/app/standalone-components/date-picker/date-picker.component';
import { InputComponent } from '@src/app/standalone-components/input/input.component';
import { IGetDriverTachoFile, IGetVehicleTachoFile } from '@src/app/interfaces/api/files-ddd.interface';
import { formatDate } from '@src/app/utils/date';
import { FilesDddService } from '@src/app/services/api/files-ddd.service';

const imports = [
  CommonModule,
  TranslateModule,
  InputComponent,
  FormsModule,
  ReactiveFormsModule,
  DatePickerComponent,
  TableModule,
  CheckboxModule,
  SkeletonModule,
  PaginatorModule,
  ButtonModule,
];

interface ITableRow {
  data: IGetDriverTachoFile | IGetVehicleTachoFile,
  isChecked: boolean;
}

@Component({
  selector: 'app-tachograph-files-ddd-drivers',
  standalone: true,
  imports,
  templateUrl: './tachograph-files-ddd-drivers.component.html',
  styleUrls: ['./tachograph-files-ddd-drivers.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TachographFilesDddDriversComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('containerList') private containerListRef: ElementRef<HTMLDivElement>;
  @ViewChild('paginator') private paginatorRef: Paginator;

  @Input() public initSearchValue: string;

  private readonly filesDddService = inject(FilesDddService);
  private readonly rendere2 = inject(Renderer2);

  public searchValueControl: FormControl = new FormControl('');
  public selectedDate: Date[];
  public currentListPageData: ITableRow[] = [];
  public isCheckedAll: boolean;

  public rowsPerPage = 20;
  public totalRecordsDisplayedList: number = 0;
  public isLoading: boolean = true;
  public listHeight: string;

  private GetFilesSubsc: Subscription;
  private ComponentSubsc: Subscription = new Subscription();

  constructor(
    private changeDetRef: ChangeDetectorRef,
  ) {
    this.setInitialFilters();
  }

  ngOnInit(): void {
    this.searchValueControl.patchValue(this.initSearchValue);
    this.getData();
  }

  ngAfterViewInit(): void {
    this.onChangeSearchValue();
    setTimeout(() => {
      this.setListHeight();
    }, 0);
  }

  ngOnDestroy(): void {
    this.GetFilesSubsc?.unsubscribe();
    this.ComponentSubsc.unsubscribe();
  }

  private get selectedDateFormated(): { DateFrom: string, DateTo: string } {
    const [startDate, endDate] = this.selectedDate;

    console.log(startDate, endDate);

    return {
      DateFrom: formatDate('yyyy-MM-dd', startDate),
      DateTo: formatDate('yyyy-MM-dd', endDate),
    }
  }

  public onChangeFilters(data: Date[]): void {
    const [from, to]  = data;
    this.selectedDate = data;
    (from && to) && this.getData();
  }

  private setInitialFilters(): void {
    this.selectedDate = [
      subDays(new Date(), 90),
      new Date(),
    ];
  }

  private onChangeSearchValue(): void {
    const sub = this.searchValueControl.valueChanges
    .pipe(
      distinctUntilChanged(),
      tap(() => this.isLoading = true),
      debounceTime(200),
    )
    .subscribe(() => {
      if (this.paginatorRef.currentPage() === 0) {
        this.onChangePaginate({ page: 0, rows: this.rowsPerPage });
        return;
      }
      this.paginatorRef.changePage(0);
    });
    this.ComponentSubsc.add(sub);
  }

  private getData(pageNumber = 1): void {
    this.isLoading = true;
    this.setTableData([]);
    this.GetFilesSubsc?.unsubscribe();

    const body = {
      PageNumber: pageNumber,
      PageSize: this.rowsPerPage,
      Phrase: this.searchValueControl.value,
      ...this.selectedDateFormated
    };

    this.GetFilesSubsc = this.filesDddService.GetDriverTachoFileList(body).subscribe({
      next: (response) => {
        this.totalRecordsDisplayedList = response.TotalCount;
        this.setTableData(response.Result);
        this.isLoading = false;
        this.changeDetRef.detectChanges();
      },
    });
  }

  private setTableData(data: IGetDriverTachoFile[]): void {
    this.currentListPageData = data.map(item => ({ data: item, isChecked: false }));
  }

  public onChangeValueCheckedAll(value: boolean): void {
    this.currentListPageData.forEach((item) => {
      item.isChecked = value;
    });
  }

  private setListHeight(): void {
    this.listHeight = (this.containerListRef.nativeElement.clientHeight - 45) + 'px';
    this.rendere2.setStyle(document.getElementsByClassName('p-datatable-table-container').item(0), 'max-height', this.listHeight);
  }

  public checkedRow(): ITableRow[] {
    return this.currentListPageData.filter(item => item.isChecked);
  }

  public downloadFiles(data: ITableRow[] = this.checkedRow()): void {
    const tachoFileIds = data.map((row) => {
      return {
        FileId: row.data.Id,
        Id: (row.data as IGetDriverTachoFile).DriverId,
      }
    });

    this.filesDddService.DownloadDriverTachoFiles({ TachoFileIds: tachoFileIds }).subscribe(response => {
      const blob = response.body;
      const contentDisposition = response.headers.get('Content-Disposition');

      let filename = 'plik';
      if (contentDisposition) {
        const matches = /filename="?([^"]+)"?/.exec(contentDisposition);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    });
  }

  public onChangePaginate(event: any): void {
    this.isCheckedAll = false;
    const pageIndex = event.page + 1;
    this.rowsPerPage = event.rows;
    this.getData(pageIndex);
  }
}
